/**
 * Feedback Sync Service
 *
 * Handles synchronization of feedback events and taste profile to/from Firestore.
 * Follows the same pattern as mealPlanSyncService.ts.
 */

import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
} from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { FeedbackEvent, TasteProfile } from '../redux/feedbackSlice';

/**
 * Save a single feedback event to Firestore.
 * Path: users/{userId}/feedbackEvents/{eventId}
 */
export const saveFeedbackEvent = async (
  userId: string,
  event: FeedbackEvent
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    return;
  }

  const docRef = doc(firestore, 'users', userId, 'feedbackEvents', event.id);
  // Firestore rejects undefined values — strip them before writing
  const cleanEvent = Object.fromEntries(
    Object.entries(event).filter(([_, v]) => v !== undefined)
  );
  await setDoc(docRef, cleanEvent);
};

/**
 * Load all feedback events for a user from Firestore.
 * Path: users/{userId}/feedbackEvents/*
 */
export const loadAllFeedbackEvents = async (
  userId: string
): Promise<FeedbackEvent[]> => {
  if (!isFirebaseEnabled() || !firestore) {
    return [];
  }

  const colRef = collection(firestore, 'users', userId, 'feedbackEvents');
  const snapshot = await getDocs(colRef);

  return snapshot.docs.map(d => d.data() as FeedbackEvent);
};

/**
 * Save the taste profile to Firestore.
 * Path: users/{userId}/tasteProfile/current
 */
export const saveTasteProfile = async (
  userId: string,
  profile: TasteProfile
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    return;
  }

  const docRef = doc(firestore, 'users', userId, 'tasteProfile', 'current');
  await setDoc(docRef, { ...profile });
};

/**
 * Load the taste profile from Firestore.
 * Path: users/{userId}/tasteProfile/current
 */
export const loadTasteProfile = async (
  userId: string
): Promise<TasteProfile | null> => {
  if (!isFirebaseEnabled() || !firestore) {
    return null;
  }

  const docRef = doc(firestore, 'users', userId, 'tasteProfile', 'current');
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data() as TasteProfile;
};
